import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useApi from './useApi';
import { fetchAiModels } from '../services/aiGovernanceService';
import { AI_MODEL_OPTIONS } from '../constants/aiModels';

/**
 * AI models available for selection, from the registry JLL Super Admin maintains.
 *
 * Falls back to the static `AI_MODEL_OPTIONS` so every model picker keeps working
 * if the registry request fails — a model dropdown must never render empty.
 *
 * @param {{ includeInactive?: boolean, enabled?: boolean }} [params]
 *   includeInactive is only honoured for JLL Super Admin (enforced server-side).
 * @returns {{ options: {value: string, label: string}[], models: object[],
 *             loading: boolean, error: string|null, usingFallback: boolean,
 *             refresh: () => Promise<void> }}
 */
export const useAiModelCatalog = ({ includeInactive = false, enabled = true } = {}) => {
  const api = useApi();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fetched = await fetchAiModels(api, { includeInactive });
      if (!mountedRef.current) return;
      setModels(fetched);
      setUsingFallback(false);
    } catch (err) {
      console.warn('Could not load the AI model registry; using built-in list:', err?.message || err);
      if (!mountedRef.current) return;
      setModels([]);
      setUsingFallback(true);
      setError(err?.message || 'Failed to load AI models');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [api, enabled, includeInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const options = useMemo(() => {
    if (!models.length) {
      return AI_MODEL_OPTIONS;
    }
    return models
      .filter((model) => model.modelCode)
      .map((model) => ({
        value: model.modelCode,
        label: model.modelName || model.modelCode,
        isActive: model.isActive !== false,
      }));
  }, [models]);

  return { options, models, loading, error, usingFallback, refresh: load };
};

export default useAiModelCatalog;
